import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './se-button.css.lit';
import { OutlineButton } from '../../base/outline-button/outline-button';
import { SlotController } from '../../controllers/slot-controller';
import { ifDefined } from 'lit/directives/if-defined.js';

export type SeButtonVariant = typeof seButtonVariants[number];

export const seButtonVariants = [
  'none',
  'primary',
  'primary-2',
  'registration',
  'secondary',
  'tertiary',
  'dropdown',
  'dropdown-nav',
  'dropdown-alt',
] as const;

export const seButtonSizes = ['slim', 'small', 'medium', 'large'] as const;
export type SeButtonSize = typeof seButtonSizes[number];
/**
 * The Saul-Ewing Button component
 * @element se-button
 * @slot - default slot, used for button text.
 * @slot left - slot used for left side icon.
 * @slot right - slot used for right side icon.
 */
@customElement('se-button')
export class SeButton extends OutlineButton {
  static styles: CSSResultGroup = [componentStyles];

  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'variant' })
  variant: SeButtonVariant = 'primary';

  /**
   * The button size to use.
   */
  @property({ type: String, attribute: 'size' })
  size: SeButtonSize = 'medium';
  /**
   * Allow download links.
   */
  @property({ type: Boolean, reflect: true }) downloadable = false;

  /**
   * Icon template
   *
   * @param exists - Whether the icon slot exists.
   * @param slot - The slot name to use.
   * @returns - The icon template.
   */
  iconTemplate(exists: boolean, slot: string): TemplateResult | null {
    if (!exists) return null;
    return html`<slot class="slot-icon" name="${slot}"></slot>`;
  }

  /**
   * Component render function
   * @returns TemplateResult
   * @todo This should utilize `outline-link` component.
   */
  render(): TemplateResult {
    return this.buttonUrl
      ? html` <a
          class="btn ${this.variant} ${this.size}"
          href=${this.buttonUrl}
          target=${ifDefined(this.buttonTarget)}
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${ifDefined(this.isDisabled)}"
          ?download=${this.downloadable}
        >
          ${this.iconTemplate(this.hasLeftIcon, 'left')}
          <slot></slot>
          ${this.iconTemplate(this.hasRightIcon, 'right')}
        </a>`
      : html`<button
          class="btn ${this.variant} ${this.size}"
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${ifDefined(this.isDisabled)}"
          aria-expanded="${ifDefined(
            this.expanded ? this.expanded : undefined
          )}"
          aria-controls="${ifDefined(this.controls)}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
          tabindex="-1"
        >
          ${this.iconTemplate(this.hasLeftIcon, 'left')}
          <slot></slot>
          ${this.iconTemplate(this.hasRightIcon, 'right')}
        </button> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-button': SeButton;
  }
}
